import React, {
  FC,
  useState,
  useRef,
  useMemo,
  useEffect,
  ReactNode,
  useCallback
} from 'react';

import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import parse from 'autosuggest-highlight/parse';

import throttle from 'lodash/throttle';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';

import { PlaceType, loadGoogleMapScript } from '../../../core/GoogleMaps';

const autocompleteService = { current: null };
// const placesService = { current: null };

const useStyles = makeStyles(theme => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2)
  }
}));

interface GooglePlaceAutocompleteProps {
  label?: string;
  value?: PlaceType;
  onChange?: (place?: PlaceType) => void;

  error?: boolean;
  helperText?: ReactNode;
  className?: string;
}

const GooglePlaceAutocomplete: FC<GooglePlaceAutocompleteProps> = ({
  label,
  value,
  onChange,
  error,
  helperText,
  className
}) => {
  const classes = useStyles();
  const [place, setPlace] = useState(value);
  const [options, setOptions] = useState<PlaceType[]>([]);
  const loaded = useRef(false);
  const inputRef = useRef(null);

  if (!loaded.current) {
    loadGoogleMapScript();
    loaded.current = true;
  }

  const fetch = useMemo(
    () =>
      throttle((input: any, callback: any) => {
        (autocompleteService.current as any).getPlacePredictions(
          input,
          callback
        );
      }, 700),
    []
  );

  const delayedQuery = useCallback(
    debounce(() => {
      fetch({ input: place?.description }, (results?: PlaceType[]) => {
        setOptions(results || []);
      });
    }, 1500),
    [place, fetch, setOptions]
  );

  // Watch for inputValue change and trigger a places autocomplete
  // search request which's response will be use as dropdown options
  useEffect(() => {
    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (window as any).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    const input = place?.description;
    if (isEmpty(input)) {
      setOptions([]);
      return undefined;
    }
    delayedQuery();

    return delayedQuery.cancel;
  }, [place, fetch, delayedQuery]);

  // HACK: disable autocomplete on input text field
  useEffect(() => {
    if (inputRef?.current) {
      (inputRef.current as any).autocomplete = 'off';
    }
  }, [inputRef]);

  // Render a single option from a PlaceType
  const renderOption = (pt: PlaceType) => {
    const { structured_formatting } = pt;
    if (!structured_formatting) {
      return null;
    }

    const matches = structured_formatting.main_text_matched_substrings;
    const parts = parse(
      structured_formatting.main_text,
      matches.map((match: any) => [match.offset, match.offset + match.length])
    );

    return (
      <Grid container alignItems="center">
        <Grid item>
          <LocationOnIcon className={classes.icon} />
        </Grid>
        <Grid item xs>
          {parts.map(
            (
              part: { highlight: any; text: ReactNode },
              index: string | number | undefined
            ) => (
              <span
                key={index}
                style={{ fontWeight: part.highlight ? 700 : 400 }}
              >
                {part.text}
              </span>
            )
          )}
          <Typography variant="body2" color="textSecondary">
            {structured_formatting.secondary_text}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  return (
    <Autocomplete
      getOptionLabel={option =>
        typeof option === 'string' ? option : option?.description ?? ''
      }
      filterOptions={x => x}
      options={options}
      includeInputInList
      disableOpenOnFocus
      noOptionsText="Please input your address"
      onChange={(_: any, value: any) => {
        setPlace({ ...value });
        if (onChange) {
          onChange(value);
        }
      }}
      inputValue={place?.description ?? ''}
      id={'google-places-autocomplete'}
      renderInput={params => {
        return (
          <TextField
            {...params}
            margin="dense"
            label={label}
            error={error}
            helperText={helperText}
            onBlur={() => {
              // Final change notification when place_id not exists
              if (onChange && isEmpty(place?.place_id)) {
                onChange(place);
              }
            }}
            fullWidth
            onChange={e => {
              setPlace({ description: e.target.value } as PlaceType);
            }}
            inputRef={inputRef}
          />
        );
      }}
      renderOption={renderOption}
    />
  );
};

export default GooglePlaceAutocomplete;
